import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { View, FlatList, StyleSheet, Platform } from 'react-native';
import { HeaderBackButton } from '@react-navigation/elements';
import {
  carSearch,
  handleFiftyMileCarSearch,
  selectCar,
  orderBy,
  filterByType,
  filterBySearchType,
} from '../store/actions/car';
import { CarCard } from '../components/resultCards';
import TabBar from '../components/SegmentedControl/TabBar';
// import FloatButton from '../components/FloatButton';
import NavigateFilter from '../components/NavigateFilter';
import SearchFabMenuModal from '../components/SearchFabMenuModal';
import BottomSheet from 'react-native-simple-bottom-sheet';
import {
  selectCarResponsesFiltered,
  selectCarFilterListByType,
  selectCarFilterByType,
  selectCarOrderBy,
  selectCarFilterListBySearchType,
  selectCarSearchType,
  selectCenterCarPosition,
  selectCarMapResponses,
} from '../store/selectors/car.selector';
import {
  selectFirstSearchCriteria,
  selectRequestComponentByComponentIndex,
} from '../store/selectors/search.selector';
import BaseText from '../components/BaseText';
import { Modals } from '../constants/ScreenNames';
import useBoolean from '../utils/useBoolean';
import { isCloseToBottom } from '../utils/screen.utils';
import { movePrevious } from '../store/actions/workflow';
import { Color, CommonProperties, CommonStyles } from '../styles';
import HeaderTitle from '../components/shared/HeaderTitle';
import { rightFilterHeaderOption } from '../utils/defaultNavOptions';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../i18n';
import MapView from 'react-native-maps';
import { scale } from 'react-native-size-matters';
import Interstitial from '../components/modals/Interstitial';
import useTextInterstitialOptions from '../utils/useTextInterstitialOptions';
import { Source, searchTypesEnum } from '../constants/Enums';
import InfoMarker, { CarMarkerContent } from '../components/maps/InfoMarker';
import useAvailableHeight from '../utils/useAvailableHeight';
import useTextOrderByCarList from '../utils/useTextOrderByCarList';
import { useDateFormat } from '../utils/useDateFormat';
import CircleSegmentedControl from '../components/SegmentedControl/CircleSegmentedControl';
import {
  selectCompanyCurrency,
  selectCompanyCurrencyCulture,
  selectTokenData,
} from '../store/selectors/auth.selector';
import Messages from '../components/event/Messages';
import { selectTezEvent, selectTezEventGroup } from '../store/selectors/events.selector';
import { pageKeys } from '../constants/PageKeys';
import { EVENTS_ENABLED } from '../config/env';
import { formatCurrency } from '../utils/currency.utils';
import Button from '../components/Button';
import Icon from '../components/Icon';

const MAX_REQUEST_ATTEMPS = 4;

const CarScreen = ({ navigation }) => {
  const { t } = useTranslation(i18nNS.car);
  const { tKey } = useComponentTKey('CarScreen');
  const dispatch = useDispatch();
  const carResponses = useSelector(selectCarResponsesFiltered);
  const carTypes = useSelector(selectCarFilterListByType);
  const carTypeSelect = useSelector(selectCarFilterByType);
  const filterListBySearchType = useSelector(selectCarFilterListBySearchType)?.map((item) => {
    const { key } = item;
    return {
      ...item,
      name: t(tKey(key)),
      desc: t(tKey(`${key}Desc`)),
    };
  });
  const searchType = useSelector(selectCarSearchType);
  const markerValues = useSelector(selectCarMapResponses);
  const [carLoading, setCarLoading] = useState(false);
  const center = useSelector(selectCenterCarPosition);
  const orderBySelected = useSelector(selectCarOrderBy);
  const searchCriteria = useSelector(selectFirstSearchCriteria);
  const requestComponent = useSelector(selectRequestComponentByComponentIndex);

  const [modalVisible, showModal, hideModal] = useBoolean(false);
  const [visibleNavigateFilter, showNavigateFilter, hideNavigateFilter] = useBoolean(false);
  const [visibleMenuButton, showMenuButton, hideMenuButton] = useBoolean(true);
  const panelRef = useRef(null);
  const iosMinimizeBtnHeight = Platform.OS === Source.IOS ? 75 : 60;
  const availableHeight = useAvailableHeight(scale(iosMinimizeBtnHeight));
  const interstitialOptions = useTextInterstitialOptions(searchTypesEnum.CAR);
  const currency = useSelector(selectCompanyCurrency);
  const currencyCulture = useSelector(selectCompanyCurrencyCulture);
  const eventGroupKey = useSelector(selectTezEventGroup);
  const tezEvent = useSelector(selectTezEvent);
  const tokenData = useSelector(selectTokenData);
  const [is50MilesAdded, setIs50MilesAdded] = useState(true);

  const flatListRef = useRef();
  const [orderByCarList] = useTextOrderByCarList();
  const formatDate = useDateFormat();

  let fromDate = '';
  let toDate = '';

  if (requestComponent?.DisplayDate) {
    const displaydateSeperated = requestComponent.DisplayDate?.split('-');
    fromDate = displaydateSeperated[0];
    toDate = displaydateSeperated[1];
  }

  const onSearchFabMenuModalPress = (value) => {
    switch (value) {
      case 0:
        navigation.navigate(Modals.CarFilterModal);
        break;
    }
    hideModal();
  };

  const setSelectedFilter = useCallback(
    (value) => {
      dispatch(filterBySearchType(value));
    },
    [dispatch]
  );

  useEffect(() => {
    async function fetchCars() {
      setCarLoading(true);
      await dispatch(carSearch(MAX_REQUEST_ATTEMPS));
      setCarLoading(false);
    }
    fetchCars();
  }, [dispatch]);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerLeft: (headerLeftProps) => (
        <HeaderBackButton {...headerLeftProps} onPress={() => dispatch(movePrevious())} />
      ),
      headerTitle: () => (
        <HeaderTitle
          title={
            carLoading
              ? t(tKey('Loading'))
              : filterListBySearchType
              ? t(tKey(searchType))
              : t(tKey('NoResults'))
          }
          subtitle={formatDate(fromDate, null, 'MMM DD') + '-' + formatDate(toDate, null, 'MMM DD')}
          onPress={filterListBySearchType && showNavigateFilter}
        />
      ),
      ...rightFilterHeaderOption(
        () => filterListBySearchType && navigation.navigate(Modals.CarFilterModal)
      ),
      headerTitleAlign: 'center',
    });
  }, [
    navigation,
    searchType,
    showNavigateFilter,
    searchCriteria,
    dispatch,
    carLoading,
    filterListBySearchType,
    t,
    tKey,
    fromDate,
    toDate,
    formatDate,
  ]);

  useEffect(() => {
    if (carResponses && flatListRef.current) {
      flatListRef.current.scrollToOffset({ animated: true, offset: 0 });
    }

    showMenuButton();
  }, [carResponses, showMenuButton]);

  const handle50MilesResults = async () => {
    setCarLoading(true);
    dispatch(handleFiftyMileCarSearch());
    setCarLoading(false);
  };

  const carResponsesByRadius = (carResponseList) => {
    const carResponseListByRadius = carResponseList;
    if (!carResponseList?.includes('')) {
      if (carResponseList?.length < 3) {
        // Append after the last item if fewer than 3 items
        carResponseListByRadius.push('');
        setIs50MilesAdded(false);
      } else if (carResponseList?.length <= 12) {
        // Insert at the fourth position otherwise
        carResponseListByRadius.splice(3, 0, '');
        setIs50MilesAdded(false);
      }
    }
    return carResponseListByRadius;
  };

  const renderCarList = ({ item }) => {
    if (item?.ResponseDetailKey) {
      return (
        <CarCard
          style={CommonStyles.boxShadow}
          item={item}
          showBrands
          disabled={item.IsSuppressRentalCar}
          currency={currency}
          currencyCulture={currencyCulture}
          alignCenterContent={true}
          onPress={() => {
            dispatch(selectCar(item));
          }}
        />
      );
    } else if (!is50MilesAdded) {
      return (
        <View style={styles.car50Container}>
          <View>
            <Icon size={28} name="car" color={Color.primary} />
          </View>
          <View style={styles.textContainer}>
            <BaseText style={CommonStyles.h3} numberOfLines={4}>
              {t(tKey('CarRadius50Mi'))}
            </BaseText>
            <Button
              onPress={handle50MilesResults}
              label={t(tKey('Search50Mi'))}
              style={styles.milesButton}
            />
          </View>
        </View>
      );
    }
  };

  return (
    <View style={styles.pageContainer}>
      <Messages
        companyKey={EVENTS_ENABLED ? tezEvent?.CompanyKey : tokenData?.companyKey}
        groupKey={EVENTS_ENABLED ? eventGroupKey : tokenData?.groupKey}
        pageKey={pageKeys.CAR_LIST}
      />
      <View style={styles.wrapper}>
        <View style={CommonStyles.flex1}>
          {carLoading ? (
            <Interstitial icon="car" options={interstitialOptions} />
          ) : (
            <>
              {carResponses && (
                <MapView
                  style={styles.mapStyle}
                  initialRegion={{
                    latitude: center.latitude,
                    longitude: center.longitude,
                    latitudeDelta: 1,
                    longitudeDelta: 1,
                  }}
                >
                  {markerValues &&
                    markerValues.map((markerValue, index) => {
                      return (
                        <InfoMarker
                          key={index}
                          latitude={Number(markerValue.MapInfo.split('|')[0])}
                          longitude={Number(markerValue.MapInfo.split('|')[1])}
                          label={formatCurrency(markerValue?.TotalFare, currency, currencyCulture)}
                          onCalloutPress={() => dispatch(selectCar(markerValue))}
                        >
                          <CarMarkerContent
                            onPress={() => dispatch(selectCar(markerValue))}
                            carResponse={markerValue}
                            currency={currency}
                            currencyCulture={currencyCulture}
                          />
                        </InfoMarker>
                      );
                    })}
                </MapView>
              )}
              <BottomSheet
                ref={(ref) => (panelRef.current = ref)}
                wrapperStyle={{
                  backgroundColor: 'transparent',
                  paddingHorizontal: 0,
                  elevation: 0,
                }}
                lineStyle={{ padding: 0, marginTop: 12, marginBottom: 12 }}
                sliderMaxHeight={availableHeight}
                sliderMinHeight={scale(100)}
              >
                {(onScrollEndDrag) => (
                  <>
                    <CircleSegmentedControl
                      items={orderByCarList}
                      onChange={(value) => dispatch(orderBy(value))}
                      selected={orderBySelected}
                    />
                    <FlatList
                      ref={flatListRef}
                      contentContainerStyle={styles.flexGrow1}
                      data={carResponsesByRadius(carResponses)}
                      keyExtractor={(item, index) => `${item?.ResponseDetailKey}${index}`}
                      renderItem={renderCarList}
                      ListEmptyComponent={() => (
                        <BaseText style={styles.noResultsText}>
                          {t(tKey('NoCarsAvailable'))}
                        </BaseText>
                      )}
                      onScroll={({ nativeEvent }) => {
                        if (isCloseToBottom(nativeEvent)) {
                          hideMenuButton();
                        } else if (!visibleMenuButton) {
                          showMenuButton();
                        }
                      }}
                      onScrollEndDrag={onScrollEndDrag}
                    />
                  </>
                )}
              </BottomSheet>
            </>
          )}
        </View>
        {(carLoading || (carTypes && carTypes.length > 1)) && (
          <TabBar
            onChange={(value) => {
              dispatch(filterByType(value));
            }}
            selected={carTypeSelect}
            items={
              carLoading
                ? [
                    { second: t(tKey('All')) },
                    { second: t(tKey('Economy')) },
                    { second: t(tKey('Compact')) },
                    { second: t(tKey('Intermediate')) },
                  ]
                : carTypes?.map((i) => {
                    i.second = t(tKey(i.key));
                    return i;
                  })
            }
            loading={carLoading}
            style={Platform.OS === Source.IOS ? styles.iosStyle : null}
          />
        )}
        {/* {visibleMenuButton && <FloatButton onPress={showModal} iconName="menu" />} */}
        <SearchFabMenuModal
          title={t(tKey('FilterCars'))}
          onPress={onSearchFabMenuModalPress}
          onClose={hideModal}
          visible={modalVisible}
        />
        <NavigateFilter
          onRequestClose={hideNavigateFilter}
          onPress={setSelectedFilter}
          selected={searchType}
          items={filterListBySearchType}
          visible={visibleNavigateFilter}
        />
      </View>
    </View>
  );
};

export const styles = StyleSheet.create({
  mapStyle: {
    ...StyleSheet.absoluteFillObject,
  },
  pageContainer: {
    flex: 1,
    backgroundColor: Color.accent,
  },
  wrapper: {
    height: '100%',
    justifyContent: 'space-between',
  },
  flexGrow1: {
    flexGrow: 1,
  },
  noResultsText: {
    textAlign: 'center',
    ...CommonProperties.mt3,
  },
  iosStyle: { height: 80, paddingBottom: 20 },
  car50Container: {
    ...CommonProperties.p4,
    ...CommonProperties.mb3,
    ...CommonProperties.mx3,
    ...CommonProperties.borderRadius,
    ...CommonProperties.shadow,
    ...CommonProperties.row,
    backgroundColor: Color.accent,
  },
  textContainer: {
    paddingLeft: 10,
    flex: 1,
    alignItems: 'center',
  },
  milesButton: {
    alignItems: 'center',
  },
});

export default CarScreen;
